import { useMemo, useState } from 'react'
import { ClipboardCheck } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { PageShell } from '@/components/common/PageShell'
import { EmptyState } from '@/components/common/EmptyState'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  DataTable,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCard,
} from '@/components/common/DataTable'
import { SectionLoader } from '@/components/common/SectionLoader'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { Badge, Button, SegmentedControl } from '@/components/ui'
import { formatCurrency } from '@/lib/formatters'
import { usePermissions } from '@/hooks/usePermissions'
import { downloadClaimReceipt } from '@/api/services/expense-claims.service'
import {
  useApproveClaim,
  useClaimApprovals,
  useMarkClaimReimbursed,
  useRejectClaim,
} from '../hooks/useExpenseClaims'
import { CLAIM_STATUS_LABEL, CLAIM_STATUS_VARIANT, payoutLabel } from '../utils/claimDisplay'
import type { ApprovalsView, ExpenseClaimView } from '../types/expenseClaim.types'
import { ClaimNoteModal, ClaimReceipts, ClaimSteps } from './ClaimParts'

const VIEW_OPTIONS: Array<{ value: ApprovalsView; label: string }> = [
  { value: 'pending', label: 'Waiting for me' },
  { value: 'decided', label: 'Decided by me' },
  { value: 'all', label: 'All claims' },
]

const EMPTY_COPY: Record<ApprovalsView, { title: string; description: string }> = {
  pending: {
    title: 'Nothing waiting for you',
    description: 'Claims appear here when you are the approver for their current step.',
  },
  decided: {
    title: 'No decisions yet',
    description: 'Claims you approve or reject are listed here.',
  },
  all: {
    title: 'No expense claims',
    description: 'Organization-wide approvers see every claim here.',
  },
}

const LIMIT = 10

type PendingAction = { kind: 'approve' | 'reject' | 'reimburse'; claim: ExpenseClaimView }

function formatDate(value?: string): string {
  if (!value) return '—'
  try {
    return format(parseISO(value), 'd MMM yyyy')
  } catch {
    return '—'
  }
}

/** Approvals inbox for both steps (manager, then admin) of an expense claim. */
export function ExpenseApprovalsPage(): React.ReactElement {
  const [view, setView] = useState<ApprovalsView>('pending')
  const [page, setPage] = useState(1)
  const [action, setAction] = useState<PendingAction | null>(null)

  const params = useMemo(() => ({ view, page, limit: LIMIT }), [view, page])
  const { data, isLoading, isError, error, refetch } = useClaimApprovals(params)
  const claims = data?.data ?? []
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  const approveMutation = useApproveClaim()
  const rejectMutation = useRejectClaim()
  const reimburseMutation = useMarkClaimReimbursed()
  const close = (): void => setAction(null)
  // Approvers are chosen by the approve permission; rejecting is a separate one.
  const canReject = usePermissions().can('expenses', 'reject')

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Expenses', to: '/expenses' }, { label: 'Expense Approvals' }]}
      icon={ClipboardCheck}
      title="Expense Approvals"
      description="Expense claims waiting for your decision."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <SegmentedControl
              ariaLabel="Which claims to show"
              options={VIEW_OPTIONS}
              value={view}
              onChange={(v: ApprovalsView) => {
                setView(v)
                setPage(1)
              }}
            />
          }
        />

        {isLoading ? (
          <DataTableCard>
            <SectionLoader variant="table" cols={6} rows={5} label="Loading claims" />
          </DataTableCard>
        ) : isError ? (
          <DataTableCard>
            <QueryErrorState
              error={error}
              title="Couldn't load expense claims"
              onRetry={refetch}
              className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
            />
          </DataTableCard>
        ) : claims.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title={EMPTY_COPY[view].title}
            description={EMPTY_COPY[view].description}
          />
        ) : (
          <DataTable>
            <DataTableHead>
              <tr>
                <DataTableHeadCell>Claimant</DataTableHeadCell>
                <DataTableHeadCell>Claim</DataTableHeadCell>
                <DataTableHeadCell align="right">Amount</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell>Approval</DataTableHeadCell>
                <DataTableHeadCell>Receipts</DataTableHeadCell>
                <DataTableHeadCell align="right">
                  <span className="sr-only">Actions</span>
                </DataTableHeadCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {claims.map((c) => (
                <DataTableRow key={c.id}>
                  <DataTableCell>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {c.claimant.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{c.companyName}</div>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="text-sm text-gray-900 dark:text-gray-100">{c.title}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {c.categoryLabel} · {formatDate(c.date)}
                    </div>
                    {c.description && (
                      <div className="mt-0.5 max-w-64 text-xs text-gray-500 dark:text-gray-400">
                        {c.description}
                      </div>
                    )}
                  </DataTableCell>
                  <DataTableCell align="right">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(c.amount, c.currency)}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="space-y-1">
                      <Badge variant={CLAIM_STATUS_VARIANT[c.status]}>
                        {CLAIM_STATUS_LABEL[c.status]}
                      </Badge>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {payoutLabel(c.payout)}
                      </div>
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <ClaimSteps steps={c.approval.steps} />
                  </DataTableCell>
                  <DataTableCell>
                    <ClaimReceipts
                      receipts={c.receipts}
                      download={(uploadId) => downloadClaimReceipt(c.id, uploadId)}
                    />
                  </DataTableCell>
                  <DataTableCell align="right">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {c.canAct && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => setAction({ kind: 'approve', claim: c })}
                          >
                            Approve
                          </Button>
                          {canReject && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setAction({ kind: 'reject', claim: c })}
                            >
                              Reject
                            </Button>
                          )}
                        </>
                      )}
                      {c.canMarkReimbursed && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setAction({ kind: 'reimburse', claim: c })}
                        >
                          Paid outside payroll
                        </Button>
                      )}
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}

        <TablePaginationBar
          page={page}
          limit={LIMIT}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <ClaimNoteModal
        open={action?.kind === 'approve'}
        onClose={close}
        title="Approve claim"
        description={
          action
            ? `${action.claim.claimant.name}: ${formatCurrency(action.claim.amount, action.claim.currency)} for ${action.claim.title}. Once fully approved it is paid back in the next payroll run.`
            : ''
        }
        label="Note (optional)"
        confirmLabel="Approve"
        minLength={0}
        loading={approveMutation.isPending}
        onConfirm={(note) => {
          if (!action) return
          approveMutation.mutate(
            { id: action.claim.id, note: note || undefined },
            { onSuccess: close }
          )
        }}
      />
      <ClaimNoteModal
        open={action?.kind === 'reject'}
        onClose={close}
        title="Reject claim"
        description="The claimant sees your reason. A rejected claim is not paid."
        label="Reason"
        confirmLabel="Reject"
        minLength={5}
        variant="danger"
        loading={rejectMutation.isPending}
        onConfirm={(reason) => {
          if (!action) return
          rejectMutation.mutate({ id: action.claim.id, reason }, { onSuccess: close })
        }}
      />
      <ClaimNoteModal
        open={action?.kind === 'reimburse'}
        onClose={close}
        title="Mark as paid outside payroll"
        description="Use this only when the money was paid back another way (for example the employee has left). Payroll will not pay this claim, and the note is kept in the audit log."
        label="How and when it was paid"
        confirmLabel="Mark reimbursed"
        minLength={10}
        loading={reimburseMutation.isPending}
        onConfirm={(note) => {
          if (!action) return
          reimburseMutation.mutate({ id: action.claim.id, note }, { onSuccess: close })
        }}
      />
    </PageShell>
  )
}

ExpenseApprovalsPage.displayName = 'ExpenseApprovalsPage'
